import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { Download, Loader2, AlertTriangle, FileMusic, Music } from 'lucide-react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { useAuth } from '../../services/auth';

interface Recording {
  id: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  originalFileName: string;
  originalFileUrl: string;     // Add this
  processedFileUrl?: string;   // This is already there
  transcript?: string;
  error?: string;
}

const BASE_URL =
  process.env.NODE_ENV === 'development'
    ? 'http://localhost:3004'
    : process.env.REACT_APP_BACKEND_URL;

// Empty state component
const EmptyState = () => (
  <div className="flex flex-col items-center justify-center p-8 text-center">
    <Music className="h-16 w-16 text-gray-400 mb-4" />
    <h3 className="text-lg font-medium text-gray-900">No recordings found</h3>
    <p className="text-sm text-gray-500 mt-2">Upload some audio files to get started.</p>
  </div>
);

const FileCardSkeleton = () => (
  <Card className="w-full">
    <CardHeader>
      <Skeleton className="h-6 w-3/4" />
    </CardHeader>
    <CardContent>
      <Skeleton className="h-4 w-1/2" />
    </CardContent>
    <CardFooter className="justify-end">
      <Skeleton className="h-9 w-32" />
    </CardFooter>
  </Card>
);

const SkeletonGrid = () => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {[...Array(6)].map((_, i) => (
      <FileCardSkeleton key={i} />
    ))}
  </div>
);

const SimpleFileCard = ({ file, onDownload }: { file: Recording; onDownload: (file: Recording) => void }) => (
  <Card className="w-full hover:shadow-lg transition-shadow duration-200">
    <CardHeader>
      <div className="flex items-center gap-3">
        <FileMusic className="h-6 w-6 text-blue-500" />
        <CardTitle className="text-lg font-medium truncate">
          {file.originalFileName}
        </CardTitle>
      </div>
    </CardHeader>
    <CardFooter className="justify-end">
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-2 hover:bg-blue-50"
        onClick={() => onDownload(file)}
      >
        <Download className="h-4 w-4" />
        Download
      </Button>
    </CardFooter>
  </Card>
);

const ProcessedFileCard = ({ file, onDownload }: { file: Recording; onDownload: (file: Recording) => void }) => (
  <Card className="w-full hover:shadow-lg transition-shadow duration-200">
    <CardHeader>
      <div className="flex items-center gap-3">
        <Music className="h-6 w-6 text-purple-500" />
        <CardTitle className="text-lg font-medium truncate">
          {file.originalFileName}
        </CardTitle>
      </div>
    </CardHeader>
    <CardContent>
      {file.transcript && (
        <div className="text-sm text-gray-500 flex items-center gap-2">
          <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
          Transcript available
        </div>
      )}
    </CardContent>
    <CardFooter className="justify-end">
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-2 hover:bg-purple-50"
        onClick={() => onDownload(file)}
      >
        <Download className="h-4 w-4" />
        Download Processed
      </Button>
    </CardFooter>
  </Card>
);

export const MyFiles = () => {
  const { refreshJWT, isAuthenticated } = useAuth();

  const { data: recordings, isLoading, error, refetch } = useQuery({
    queryKey: ['recordings', 'original'],
    queryFn: async () => {
      const token = await refreshJWT();
      try {
        const response = await axios.get<Recording[]>(`${BASE_URL}/recording/recordings`, {
          withCredentials: true,
          headers: { 'Authorization': `Bearer ${token}` }
        });
        return response.data;
      } catch (err: any) {
        console.error('API Error:', {
          status: err.response?.status,
          url: err.config?.url,
          message: err.message,
          response: err.response?.data,
        });
        throw err;
      }
    },
    enabled: isAuthenticated,
    retry: 1,
  });

  const handleDownload = async (file: Recording) => {
    try {
      const token = await refreshJWT();
      const response = await axios.get(`${BASE_URL}/recording/${file.id}`, {
        responseType: 'blob',
        withCredentials: true,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.originalFileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      toast.error('Failed to download the file.');
    }
  };

  if (error) {
    return (
      <div className="p-4 text-red-500 flex items-center gap-2">
        <AlertTriangle className="h-5 w-5" />
        An error occurred while loading recordings
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">My Files</h2>
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => refetch()}
          className="flex items-center gap-2 hover:bg-gray-50"
        >
          <Loader2 className="h-4 w-4" />
          Refresh
        </Button>
      </div>
      
      {isLoading ? (
        <div className="flex justify-center items-center h-32">
          <Loader2 className="h-8 w-8 animate-spin" />
        </div>
      ) : !recordings || recordings.length === 0 ? (
        <div className="text-gray-500 text-center">No recordings found.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recordings.map((recording) => (
            <div key={recording.id} className="border p-4 rounded-lg shadow-sm">
              <div className="font-medium text-lg">{recording.originalFileName}</div>
              <Button
                variant="default"
                size="sm"
                onClick={() => handleDownload(recording)}
                className="mt-4 w-full"
              >
                Download
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const MyCreations = () => {
  const { refreshJWT, isAuthenticated } = useAuth();

  // Use the same query as MyFiles but filter for processed files
  const { data: recordings, isLoading, error, refetch } = useQuery({
    queryKey: ['recordings', 'original'],
    queryFn: async () => {
      const token = await refreshJWT();
      try {
        const response = await axios.get<Recording[]>(`${BASE_URL}/recording/recordings`, {
          withCredentials: true,
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        // Filter to only show recordings that have a processed file
        return response.data.filter(recording => 
          recording.processedFileUrl
        );
      } catch (err: any) {
        console.error('API Error:', {
          status: err.response?.status,
          url: err.config?.url,
          message: err.message,
          response: err.response?.data,
        });
        return [];
      }
    },
    enabled: isAuthenticated,
    retry: 1,
  });

  const handleDownload = async (recording: Recording) => {
    if (!recording.processedFileUrl) return;
    
    try {
      const token = await refreshJWT();
      const response = await axios.get(`${BASE_URL}/recording/${recording.id}`, {
        responseType: 'blob',
        withCredentials: true,
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `processed_${recording.originalFileName}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download started!');
    } catch (error: any) {
      console.error('Download error:', error);
      toast.error('Failed to download the file');
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">My Creations</h2>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="flex items-center gap-2 hover:bg-gray-50"
        >
          <Loader2 className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {isLoading ? (
        <SkeletonGrid />
      ) : error ? (
        <div className="p-4 text-red-500 flex items-center gap-2">
          <AlertTriangle className="h-5 w-5" />
          Failed to load recordings
        </div>
      ) : !recordings || recordings.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {recordings.map((recording) => (
            <ProcessedFileCard
              key={recording.id}
              file={recording}
              onDownload={handleDownload}
            />
          ))}
        </div>
      )}
    </div>
  );
};